<template>
	<div class="checkout-container">
		<div class="checkout-container-left">
			<div v-if="invoice" class="invoice-items-container">
				<div v-for="invoiceItem in invoiceFilteredItems" v-bind:key="invoiceItem.invoice_item_id" class="invoice-item">
					<img class="invoice-item-thumbnail" v-bind:src="invoiceItem.image"
						@click="goToInventoryItem(invoiceItem.url)" />
					<p>{{ invoiceItem.description }}</p>
					<label>{{
						invoiceItem.display_hammer_price.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})
					}}</label>
				</div>
			</div>

			<!-- Auction Items Total Bottom Section -->
			<div class="auction-item-total-container">
				<div>
					<label class="title">Lot Hammer</label>
					<label class="dollar-value">${{
						invoiceHammerTotal.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})
					}}</label>
				</div>
				<div>
					<label class="title">Buyer's Premium</label>
					<label class="dollar-value">${{
						buyersPremiumTotal.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})
					}}</label>
				</div>
				<div>
					<label class="title">Auction Items Total</label>
					<label class="dollar-value">${{
						itemsTotal.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})
					}}</label>
				</div>
				<div>
					<p>*Total does not include applicable sales tax</p>
				</div>
			</div>
		</div>
		<div class="checkout-container-right">
			<!-- Top Header -->
			<div class="top-header-container">
				<img class="logo largeLogo" src="/images/dark-logo.svg" alt="Leland Little Auctions" @click="exit" />
				<label>AUCTION ITEM CHECKOUT</label>
			</div>

			<div class="details-container">
				<div class="breadcrumb-container">
					<div v-for="(crumb, index) in breadCrumbs" v-bind:key="crumb.title" class="breadcrumb-item">
						<p v-bind:class="{
							'breadcrumb-item-active': crumb.active,
							'breadcrumb-item-clickable': !crumb.active && crumb.canNavigate,
							'breadcrumb-item-unclickable':
								!crumb.active && !crumb.canNavigate
						}" @click="goToRoute(crumb.route, crumb.canNavigate)">
							{{ crumb.title }}
						</p>
						<span v-if="index != breadCrumbs.length - 1"> / </span>
					</div>
				</div>

				<div class="details-inner-container">
					<h1>Shipping Address</h1>
					<p class="shipping-address-subtitle">
						Complete the shipping information to receive shipping options for
						your items.
					</p>

					<!-- Radio Button Options -->
					<div class="radio-options-container">
						<!-- Option 1 -->
						<div class="radio-option">
							<input v-model="radioPicked" type="radio" v-bind:disabled="isThinking" v-bind:value="0" checked />
							<label>Ship to me</label>
						</div>

						<!-- Option 2 -->
						<div class="radio-option">
							<input v-model="radioPicked" type="radio" v-bind:disabled="isThinking" v-bind:value="1" />
							<label>Pick up</label>
						</div>
					</div>

					<!-- Ship to me section -->
					<div v-if="radioPicked == 0">
						<!-- Customer Name -->
						<div class="customer-name-container">
							<label>Name</label>
							<input v-model="contacts.name" placeholder="Customer Name" type="text"
								:class="!nameValid ? 'error' : ''" />
							<div v-if="!nameValid" class="error">Name is required</div>
						</div>

						<!-- Address -->
						<div class="address-line-1-container">
							<label>Address</label>
							<input v-model="rawAddress.street_1" placeholder="Address" type="text"
								:class="!street1Valid ? 'error' : ''" />
							<div v-if="!street1Valid" class="error">{{ street1Error }}</div>
						</div>

						<!-- Address 2 -->
						<div class="address-line-2-container">
							<label>Address 2</label>
							<input v-model="rawAddress.street_2" placeholder="Address Line 2" type="text"
								:class="!street2Valid ? 'error' : ''" />
							<div v-if="!street2Valid" class="error">{{ street2Error }}</div>
						</div>

						<!-- Address 3 -->
						<div class="address-line-3-container">
							<label>Address 3</label>
							<input v-model="rawAddress.street_3" placeholder="Address Line 3" type="text"
								:class="!street3Valid ? 'error' : ''" />
							<div v-if="!street3Valid" class="error">{{ street3Error }}</div>
						</div>

						<!-- City -->
						<div class="city-container">
							<label>City</label>
							<input v-model="rawAddress.city" placeholder="City" type="text" :class="!cityValid ? 'error' : ''" />
							<div v-if="!cityValid" class="error">City is required</div>
						</div>

						<!-- State, Postal Code, Country -->
						<div class="address-state-container">
							<div v-if="rawAddress.country !== 'US'" class="state">
								<label>State/Prov</label>
								<input v-model="rawAddress.state" placeholder="State" type="text" :class="!stateValid ? 'error' : ''" />
								<div v-if="!stateValid" class="error">State is required</div>
							</div>
							<div v-else class="state">
								<label>State/Prov</label>
								<select v-model="rawAddress.state" :class="!stateValid ? 'error' : ''" @blur="validateInputs">
									<option v-for="state in sortedStates" v-bind:key="state.code" v-bind:value="state.code"
										@index="state.code">{{ state.name }}</option>
								</select>
								<div v-if="!stateValid" class="error">State is required</div>
							</div>

							<div class="postal-code">
								<label>Postal&nbsp;Code</label>
								<input v-model="rawAddress.zip" placeholder="Postal Code" type="text"
									:class="!zipValid ? 'error' : ''" />
								<div v-if="!zipValid" class="error">
									Postal Code is required
								</div>
							</div>

							<div class="country">
								<label>Country</label>
								<select v-model="rawAddress.country" :class="!countryValid ? 'error' : ''" @blur="validateInputs">
									<option v-for="country in sortedCountries" v-bind:key="country.iso" v-bind:value="country.iso"
										@index="country.iso">{{ country.name }}</option>
								</select>
								<div v-if="!countryValid" class="error">
									Country is required
								</div>
							</div>
						</div>

						<!-- Phone Number -->
						<div class="phone-number-container">
							<label>Phone Number</label>
							<input v-model="contacts.phone_number" placeholder="123-456-7890" type="text"
								:class="!phoneNumberValid ? 'error' : ''" />
							<div v-if="!phoneNumberValid" class="error">
								Phone Number is required
							</div>
						</div>

						<!-- Shipping Insurance -->
						<div class="shipping-insurance-container">
							<input type="checkbox" :checked="insurance === 'arta_transit_insurance'" @click="toggleInsurance" />
							<label>Include Full Risk Insurance <span class="signature-disclaimer">(*Signature required upon
									delivery)</span></label>
						</div>

						<button v-if="isThinking" class="disabledButton continue-btn" disabled="disabled">
							<span class="loading" style="display: inline-block;"></span>
						</button>
						<button v-else class="continue-btn" :disabled="validateInputs() === false" @click="goToShippingOptions()">
							Continue
						</button>
					</div>

					<!-- Pick up section -->
					<div v-if="isPickup" class="pickup-section">
						<!-- <label>Please let us know who will be arriving for pick up by selecting one of the below options:</label> -->
						<!-- Confirm Pick Up -->
						<!-- <button v-if="isThinking" class="disabledButton continue-btn" disabled="disabled" >
                    <span class="loading" style="display: inline-block;"></span>
                  </button>
                  <button v-else class="continue-btn" @click="pickupAndGoToPayment()">
                    Continue to Payment
                  </button> -->
						<div>
							<p>
								Please let us know who will be arriving for pick up by selecting
								one of the below options:
							</p>

							<!-- The options -->
							<div class="marginNormalBottom">
								<div class="row">
									<div class="radio-column">
										<input id="customer_pickup" v-model="pickupType" class="hiddenInput" name="pickup_type"
											value="CUSTOMER_PICKUP" type="radio" @click="clickInPerson" />
										<label for="customer_pickup" class="radioCheckboxOutline">
											<div class="radioCheckbox">
												<div class="radioCheck"></div>
											</div>
										</label>
									</div>
									<div>
										<label class="minorTitle paddingNoneTop paddingSmallBottom" for="customer_pickup">
											I will pick them up myself.
										</label>
										<p>
											Tell us when you plan on picking up your items, so we may
											have a notation in our system. You can skip this step if
											you don't know, though storage fees may apply.
										</p>
									</div>
								</div>
								<div class="row marginNormalBottom">
									<div class="radio-column">
										<input id="vendor_pickup" v-model="pickupType" class="hiddenInput" name="pickup_type"
											value="THIRD_PARTY" type="radio" @click="clickThirdParty" />
										<label for="vendor_pickup" class="radioCheckboxOutline">
											<div class="radioCheckbox">
												<div class="radioCheck"></div>
											</div>
										</label>
									</div>
									<div>
										<label class="minorTitle paddingNoneTop paddingSmallBottom" for="vendor_pickup">
											I will send someone to pick them up for me.
										</label>
										<p>
											Please provide the Full Name of the person or Shipping
											Company that you authorize to pick up your items on your
											behalf. This information is needed before we can release
											your property.
										</p>
										<div class="marginNormalTop">
											<div class="paddingNormalBottom">
												Name of Individual or Shipping Company
											</div>
											<input id="pickup_person" ref="pickup_person" v-model="pickupPerson" type="text"
												name="pickup_person" :class="!pickupPersonValid ? 'error' : ''" @click="clickPickupPerson" />
											<div v-if="!pickupPersonValid" class="error">Name is required</div>
										</div>
										<div v-show="pickupType === 'THIRD_PARTY'" class="marginNormalTop">
											<p>
												Please let us know the destination address where this party will deliver your property.
											</p>
											<!-- Address -->
											<div class="address-line-1-container">
												<label>Address</label>
												<input v-model="rawAddress.street_1" placeholder="Address" type="text"
													:class="!street1Valid ? 'error' : ''" />
												<div v-if="!street1Valid" class="error">{{ street1Error }}</div>
											</div>

											<!-- Address 2 -->
											<div class="address-line-2-container">
												<label>Address 2</label>
												<input v-model="rawAddress.street_2" placeholder="Address Line 2" type="text"
													:class="!street2Valid ? 'error' : ''" />
												<div v-if="!street2Valid" class="error">{{ street2Error }}</div>
											</div>

											<!-- Address 3 -->
											<div class="address-line-3-container">
												<label>Address 3</label>
												<input v-model="rawAddress.street_3" placeholder="Address Line 3" type="text"
													:class="!street3Valid ? 'error' : ''" />
												<div v-if="!street3Valid" class="error">{{ street3Error }}</div>
											</div>

											<!-- City -->
											<div class="city-container">
												<label>City</label>
												<input v-model="rawAddress.city" placeholder="City" type="text"
													:class="!cityValid ? 'error' : ''" />
												<div v-if="!cityValid" class="error">City is required</div>
											</div>

											<!-- State, Postal Code, Country -->
											<div class="address-state-container">
												<div v-if="rawAddress.country !== 'US'" class="state">
													<label>State/Prov</label>
													<input v-model="rawAddress.state" placeholder="State" type="text"
														:class="!stateValid ? 'error' : ''" />
													<div v-if="!stateValid" class="error">State is required</div>
												</div>
												<div v-else class="state">
													<label>State/Prov</label>
													<select v-model="rawAddress.state" :class="!stateValid ? 'error' : ''" @blur="validateInputs">
														<option v-for="state in sortedStates" v-bind:key="state.code" v-bind:value="state.code"
															@index="state.code">{{ state.name }}</option>
													</select>
													<div v-if="!stateValid" class="error">State is required</div>
												</div>

												<div class="postal-code">
													<label>Postal&nbsp;Code</label>
													<input v-model="rawAddress.zip" placeholder="Postal Code" type="text"
														:class="!zipValid ? 'error' : ''" />
													<div v-if="!zipValid" class="error">
														Postal Code is required
													</div>
												</div>

												<div class="country">
													<label>Country</label>
													<select v-model="rawAddress.country" :class="!countryValid ? 'error' : ''"
														@blur="validateInputs">
														<option v-for="country in sortedCountries" v-bind:key="country.iso" v-bind:value="country.iso"
															@index="country.iso">{{ country.name }}</option>
													</select>
													<div v-if="!countryValid" class="error">
														Country is required
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div class="paddingLargeBottom">
								<button
									v-if="pickupType === '' || (pickupType === 'THIRD_PARTY' && validateThirdPartyPickupDeliveryAddress() === false)"
									class="bidButton disabledButton">
									Confirm Pick-up Plans
								</button>
								<button v-else-if="
									typeof apiPending.process_pickup_arrangement !== 'undefined'
								" class="disabledButton bidButton u-full-width" disabled="disabled">
									<span class="loading"> </span>
								</button>
								<button v-else type="submit" class="bidButton backgroundColorDefault u-full-width"
									@click="pickupAndGoToPayment">
									Confirm Pick-up Plans
								</button>
							</div>
							<div class="colorGrey">
								<p>
									Your third-party may require a signature on a bill of lading
									or additional contract terms when they pick up your item(s)
									from our gallery.
									<br /><br />
									By submitting this release, you authorize LLA to act as your
									agent to sign any such documents required, and agree to fully
									indemnify LLA for any claims made by your third-party. For
									non-North Carolina residents, please confirm that your
									authorized party is a certified transportation vendor or that
									you have a tax exemption form on file. Without these
									certifications, LLA will need to include sales tax on your
									invoice.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Bottom Footer -->
			<div class="bottom-footer-container">
				<p v-if="isStaffView" class="staff-view-banner">STAFF VIEW: Viewing invoice for {{ customerName }}</p>
				<div class="bottom-disclaimer">
					If you have any questions, please contact us here:
					<a href="mailto:info@lelandlittle.com">info@lelandlittle.com </a>
				</div>
				<button @click="exit()">Exit</button>
			</div>
		</div>

		<VerifyShippingComponent v-if="showVerifyShippingAddresses" v-bind:shippingAddresses="verifiedCandidates"
			@clicked="onClickedVerifyShipping"></VerifyShippingComponent>
		<LoadingOverlayComponent v-if="isThinking" v-bind:title="loadingTitle"></LoadingOverlayComponent>
		<AlertComponent v-if="showAlert" v-bind:title="alertTitle" v-bind:message="alertMessage"
			v-bind:secondaryAction="alertSecondaryAction" @close="exit()"></AlertComponent>
		<AlertComponent v-show="error.show" v-bind:title="error.title" v-bind:message="error.message" @close="closeError()">
		</AlertComponent>
	</div>
</template>

<script>
import VerifyShippingComponent from '@/components/Checkout/VerifyShippingComponent.vue'
import AlertComponent from '@/components/Shared/AlertComponent.vue'
import apiMixin from '@/mixins/apiMixin.js'
import InvoiceValidateMixin from '@/mixins/InvoiceValidateMixin.js'
import LoadingOverlayComponent from '@/components/Shared/LoadingOverlayComponent.vue'
import { COUNTRIES, USA_STATES } from '@/constants'
import { isEqual } from 'lodash'

/**
 * The Shipping Checkout component.
 * TODO: refactor, clean up, and document how it works
 * @displayName Shipping Component
 */
export default {
	name: 'ShippingComponent',
	components: {
		VerifyShippingComponent,
		AlertComponent,
		LoadingOverlayComponent
	},
	mixins: [apiMixin, InvoiceValidateMixin],
	data() {
		var addressFields = [
			'street_1',
			'street_2',
			'street_3',
			'city',
			'state',
			'zip',
			'country'
		]
		var contactFields = ['name', 'email_address', 'phone_number']
		var contacts = {}
		var rawAddress = {}
		var verifiedAddress = {
			isReady: false
		}

		var i, field

		var storeAddress = this.$store.getters.shippingAddress

		for (i = 0; i < addressFields.length; i++) {
			field = addressFields[i]
			rawAddress[field] = ''
			verifiedAddress[field] = ''
			if (typeof storeAddress[field] === typeof 'asdf') {
				rawAddress[field] = storeAddress[field]
			}
		}
		for (i = 0; i < contactFields.length; i++) {
			field = contactFields[i]
			contacts[field] = ''
			if (
				typeof storeAddress.contacts !== 'undefined' &&
				typeof storeAddress.contacts[field] === typeof 'asdf'
			) {
				contacts[field] = storeAddress.contacts[field]
			}
		}
		for (i = 0; i < COUNTRIES.length; i++) {
			if (COUNTRIES[i].name === rawAddress.country) {
				rawAddress.country = COUNTRIES[i].iso
			}
		}

		let error = {
			show: false,
			fatal: false,
			title: '',
			message: ''
		}

		let nameValid = true
		let phoneNumberValid = true
		let street1Valid = true
		let street2Valid = true
		let street3Valid = true
		let street1Error = ''
		let street2Error = ''
		let street3Error = ''
		let cityValid = true
		let stateValid = true
		let zipValid = true
		let countryValid = true
		let pickupPersonValid = true

		let isPickupRadio = 0
		if (this.$route.params.radioPicked === 1) {
			isPickupRadio = 1
		}
		return {
			radioPicked: isPickupRadio,
			showVerifyShippingAddresses: false,
			errors: [],
			submitStatus: null,
			countries: COUNTRIES,
			addressFields: addressFields,
			contactFields: contactFields,
			rawAddress: rawAddress,
			verifiedAddress: verifiedAddress,
			contacts: contacts,
			verifiedCandidates: [],
			error: error,
			nameValid: nameValid,
			phoneNumberValid: phoneNumberValid,
			street1Valid: street1Valid,
			street2Valid: street2Valid,
			street3Valid: street3Valid,
			street1Error: street1Error,
			street2Error: street2Error,
			street3Error: street3Error,
			cityValid: cityValid,
			stateValid: stateValid,
			zipValid: zipValid,
			countryValid: countryValid,
			loadingTitle: 'Verifying your shipping options',
			pickupPerson: '',
			pickupType: '',
			insurance: 'arta_transit_insurance',
			startedThinking: false,
			stoppingThinking: false,
			stoppedThinking: false,
			useArta: true,
			showAlert: false,
			alertTitle: '',
			alertMessage: 'If you have any questions, please contact us here: info@lelandlittle.com',
			alertSecondaryAction: null,
			pickupPersonValid: pickupPersonValid
		}
	},
	computed: {
		breadCrumbs: function () {
			return [
				{
					active: false,
					canNavigate: true,
					title: 'Auction Items',
					route: 'auctionItems'
				},
				{
					active: true,
					canNavigate: false,
					title: 'Shipping',
					route: 'shipping'
				},
				{
					active: false,
					canNavigate: false,
					title: 'Options',
					route: 'shippingOptions'
				},
				{
					active: false,
					canNavigate: false,
					title: 'Payment Info',
					route: 'paymentInfo'
				}
			]
		},
		/**
		 * Computed sort of states
		 */
		sortedStates: function () {
			return USA_STATES.sort(function (a, b) {
				let aUp = a.name.toUpperCase()
				let bUp = b.name.toUpperCase()
				if (aUp < bUp) {
					return -1
				}
				if (aUp > bUp) {
					return 1
				}
				return 0
			})
		},
		/**
		 * Computed sort of countries
		 */
		sortedCountries: function () {
			return COUNTRIES.sort(function (a, b) {
				let aUp = a.name.toUpperCase()
				let bUp = b.name.toUpperCase()
				if (aUp < bUp) {
					return -1
				}
				if (aUp > bUp) {
					return 1
				}
				return 0
			})
		},
		/**
		 * Assert if staff is viewing invoice for other customer
		 * TODO: move to mixin?
		 * @public
		 */
		isStaffView: function () {
			// If invoice customer id is not equal to logged in customer and logged in customer is staff
			// (api will not return invoice unless authenticated customer id is same as invoice id OR authenticated customer is staff)
			if (this.invoice && this.invoice.customer_id && this.$store.getters.customer.customer_id !== this.invoice.customer_id && typeof this.apiResponse.get_customer_invoice !== 'undefined' && typeof this.apiResponse.get_account !== 'undefined') {
				return true
			}
			return false
		},
		/**
		 * The display name of the customer that owns the invoice.
		 * @public
		 */
		customerName: function () {
			return this.invoice ? this.invoice.customer_display_name : ''
			// return this.$store.getters.customer.display_name
		},
		/**
		 * The API response object
		 * TODO: can move to methods?
		 * @public
		 */
		apiResponse: function () {
			return this.$store.getters.apiResponse
		},
		/**
		 * The total on the invoice without tax
		 * @public
		 */
		itemsTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_total_no_tax) {
				return parseFloat(invoice.invoice_total_no_tax).toFixed(2)
			}

			return parseFloat(0).toFixed(2)
		},
		/**
		 * The buyer's premium total on the invoice without tax
		 * @public
		 */
		buyersPremiumTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_buyers_total) {
				return invoice.invoice_buyers_total
			}

			return 0
		},
		/**
		 * Compute the hammer total for the invoice
		 * @public
		 */
		invoiceHammerTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_hammer_total) {
				return invoice.invoice_hammer_total
			}

			return 0
		},
		/**
		 * Get all the customer invoices
		 * @public
		 */
		invoices: function () {
			return this.$store.getters.invoices
		},
		/**
		 *
		 * @public
		 */
		invoice: function () {
			if (this.invoiceIsLoading) {
				return
			}
			const key = `INVOICEID_${this.currentInvoiceId}`
			const tempInvoice = this.invoices[key]
			// If tempInvoice is undefined we return false to trigger the invoice watcher
			return typeof tempInvoice !== 'undefined' ? tempInvoice : false
		},
		/**
		 * Get the current invoice id
		 * @public
		 */
		invoiceFilteredItems: function () {
			return this.invoice ? this.invoice.invoice_items.filter(item => item.inventory_id) : []
		},
		/**
		 * @public
		 */
		invoiceIsLoading: function () {
			return this.$store.state.invoiceIsLoading
		},
		/**
		 * @public
		 */
		accountIsLoading: function () {
			return this.$store.state.accountIsLoading
		},
		/**
		 * @public
		 */
		addressIsValidating: function () {
			return this.$store.state.addressIsValidating
		},
		/**
		 * @public
		 */
		artaRequestQuotesIsLoading: function () {
			return this.$store.state.artaRequestQuotesIsLoading
		},
		/**
		 * @public
		 */
		pickupNoShippingIsLoading: function () {
			return this.$store.state.pickupNoShippingIsLoading
		},
		/**
		 * Get the inventory ids for the invoice.
		 * TODO: can do with Array.map
		 * @public
		 */
		inventory_ids: function () {
			let tempInvoice = this.invoice
			let out = []
			if (tempInvoice && typeof tempInvoice.invoice_items === 'undefined') {
				return out
			}
			if (tempInvoice && tempInvoice.invoice_items) {
				for (let i = 0; i < tempInvoice.invoice_items.length; i++) {
					let item = tempInvoice.invoice_items[i]
					if (item.inventory_id) {
						out.push(item.inventory_id)
					}
				}
			}
			return out
		},
		/**
		 * If is a pickup order
		 * @public
		 */
		isPickup: function () {
			return Number(this.radioPicked) === 1
		},
		/**
		 * Complex computation of 'are we thinking'
		 * @public
		 */
		isThinking: function () {
			// refactor
			if (this.error.show) {
				return true
			}
			if (this.accountIsLoading) {
				return true
			}
			if (this.addressIsValidating) {
				return true
			}
			if (this.pickupNoShippingIsLoading) {
				return true
			}
			if (this.invoiceIsLoading) {
				return true
			}
			if (this.artaRequestQuotesIsLoading) {
				return true
			}
			return false
		},
		/**
		 * Get the invoice id of current invoice
		 * @public
		 */
		currentInvoiceId: function () {
			return this.$store.getters.checkoutInvoiceId
		},
		/**
		 * Get the apiPending object
		 * @public
		 */
		apiPending: function () {
			return this.$store.getters.apiPending
		}
	},
	watch: {
		invoice: function (invoice, prevInvoice) {
			const isLoaded = !this.invoiceIsLoading
			if (!isEqual(prevInvoice, invoice)) {
				// Invoice is loaded, load default address if address is empty.
				if (isLoaded) {
					this.loadDefaultAddress()
				}
				// Invoice changed, validate it
				if (isLoaded) {
					this.validateInvoice(invoice)
				}
			}
		},
		/**
		 * Set address to not verified if it changes
		 * @public
		 */
		rawAddress: {
			handler: function (newVal, oldVal) {
				this.verifiedAddress.isReady = false // whenever rawAddress is changed, flag verifiedAddress as needing to be verified again
			},
			deep: true
		},
		/**
		 * TODO: what does this do?
		 * @public
		 */
		stoppedThinking: {
			handler: function (newVal, oldVal) {
				if (newVal && !oldVal) {
					this.loadDefaultAddress()
				}
			}
		}
	},
	mounted() {
		if (this.invoice) {
			// Do this when coming from auction items component
			// to force normalization on initial address country/state
			this.loadDefaultAddress()
		}
		this.validateInputs()
	},
	methods: {
		/**
		 * Utility method
		 * @public
		 */
		isEmpty(str) {
			return !str || str.trim().length === 0
		},
		/**
		 * Gets called on mount, on Continue click, and when some inputs change
		 * @public
		 */
		validateInputs() {
			let allValid = true

			if (!this.isEmpty(this.contacts.name)) {
				this.nameValid = true
			} else {
				allValid = false
				this.nameValid = false
			}

			if (!this.isEmpty(this.contacts.phone_number)) {
				this.phoneNumberValid = true
			} else {
				allValid = false
				this.phoneNumberValid = false
			}

			if (this.isEmpty(this.rawAddress.street_1)) {
				this.street1Valid = false
				this.street1Error = 'Address is required'
				allValid = false
			} else {
				if (this.checkForPOBox(this.rawAddress.street_1)) {
					this.street1Valid = false
					this.street1Error = 'Cannot ship to a PO Box, please use another address'
					allValid = false
				} else {
					this.street1Valid = true
				}
			}

			if (this.isEmpty(this.rawAddress.street_2)) {
				this.street2Valid = true
			} else {
				if (this.checkForPOBox(this.rawAddress.street_2)) {
					this.street2Valid = false
					this.street2Error = 'Cannot ship to a PO Box, please use another address'
					allValid = false
				} else {
					this.street2Valid = true
				}
			}

			if (this.isEmpty(this.rawAddress.street_3)) {
				this.street3Valid = true
			} else {
				if (this.checkForPOBox(this.rawAddress.street_3)) {
					this.street3Valid = false
					this.street3Error = 'Cannot ship to a PO Box, please use another address'
					allValid = false
				} else {
					this.street3Valid = true
				}
			}

			if (!this.isEmpty(this.rawAddress.city)) {
				this.cityValid = true
			} else {
				allValid = false
				this.cityValid = false
			}

			if (!this.isEmpty(this.rawAddress.state)) {
				this.stateValid = true
			} else {
				allValid = false
				this.stateValid = false
			}

			if (!this.isEmpty(this.rawAddress.zip)) {
				this.zipValid = true
			} else {
				allValid = false
				this.zipValid = false
			}

			/*
			if (!this.isEmpty(this.rawAddress.country)) {
				this.countryValid = true
			} else {
				allValid = false
				this.countryValid = false
			}
			*/

			let countryIsIso = this.sortedCountries.filter(option => {
				return option.iso === this.rawAddress.country
			}).length > 0
			if (this.rawAddress.country !== undefined && this.rawAddress.country !== '' && countryIsIso) {
				this.countryValid = true
			} else {
				this.rawAddress.country = ''
				this.countryValid = false
				allValid = false
			}
			return allValid
		},
		// Tests if given field has PO Box
		checkForPOBox: function (field) {
			// Format string to search through
			let searchString = field.toLowerCase().replaceAll('.', '').replaceAll(/\s/g, '')
			let result = searchString.search('pobox')
			if (result === -1) {
				return false
			} else {
				return true
			}
		},
		/**
		 * Gets called by loadDefaultAddress() to normalize the state
		 * @param {string} state
		 */
		normalizeState: function (state) {
			// If the provided state is equal to or longer than two letters, try to match from US states array.
			if (state.length >= 2) {
				for (let i = 0; i < USA_STATES.length; i++) {
					if (USA_STATES[i].name.toLowerCase() === state.toLowerCase() || USA_STATES[i].code.toLowerCase() === state.toLowerCase()) {
						return USA_STATES[i].code
					}
				}
			}
			// Otherwise return empty string.
			return ''
		},
		/**
		 * Gets called by loadDefaultAddress() to normalize the country
		 * @param {string} country
		 */
		normalizeCountry: function (country) {
			// If the provided country is similar to united states or empty, we return US, the two-letter ISO code
			switch (true) {
				case country.toLowerCase().includes('united state'):
				case country.toLowerCase().trim() === 'us':
				case country.toLowerCase().trim() === 'usa':
				case country.toLowerCase().trim().length === 0:
					return 'US'
				default:
					return country
			}
		},
		/**
		 * Will reset shipping address to default if not set
		 * TODO: get from invoice, not from current signed in user
		 * @public
		 */
		loadDefaultAddress: function () {
			var i, field
			// don't load default address if we already have values in place
			for (i = 0; i < this.addressFields.length; i++) {
				field = this.addressFields[i]
				if (this.rawAddress[field].length) {
					// Normalize the country and state
					this.rawAddress.country = this.normalizeCountry(this.rawAddress.country)
					this.rawAddress.state = this.normalizeState(this.rawAddress.state)
					return
				}
			}
			// TODO: If we have contact fields, we return nothing?
			for (i = 0; i < this.contactFields.length; i++) {
				field = this.contactFields[i]
				if (this.contacts[field].length) {
					return
				}
			}
			// We set the default address to the customer billing address on the invoice object
			const storeAddress = {
				city: this.invoice.customer_billing_city,
				contacts: {
					phone_number: this.invoice.customer_phone,
					email_address: this.invoice.customer_email,
					name: this.invoice.customer_display_name
				},
				country: this.normalizeCountry(this.invoice.customer_billing_country),
				state: this.normalizeState(this.invoice.customer_billing_state),
				street_1: this.invoice.customer_billing_street_address,
				street_2: undefined,
				street_3: undefined,
				zip: this.invoice.customer_billing_zip
			}

			for (i = 0; i < this.addressFields.length; i++) {
				field = this.addressFields[i]
				this.rawAddress[field] = ''
				this.verifiedAddress[field] = ''
				if (typeof storeAddress[field] === typeof 'asdf') {
					this.rawAddress[field] = storeAddress[field]
				}
			}
			for (i = 0; i < this.contactFields.length; i++) {
				field = this.contactFields[i]
				this.contacts[field] = ''
				if (typeof storeAddress.contacts !== 'undefined' && typeof storeAddress.contacts[field] === typeof 'asdf') {
					this.contacts[field] = storeAddress.contacts[field]
				}
			}
			for (i = 0; i < this.sortedCountries.length; i++) {
				if (this.sortedCountries[i].name === this.rawAddress.country) {
					this.rawAddress.country = this.sortedCountries[i].iso
				}
			}
			this.validateInputs()
		},
		/**
		 * Gets called when user clicks to Confirm Pick-up Plans
		 * @public
		 */
		pickupAndGoToPayment() {
			let data = {
				pickupType: this.pickupType,
				pickupPerson: this.pickupPerson,
				inventory_ids: this.inventory_ids
			}
			if (this.pickupType === 'THIRD_PARTY') {
				// We require an address now. Validate it if not yet valid
				if (!this.verifiedAddress.isReady) {
					return this.avalaraValidateAddress()
				}
				// We send the address too
				data = { ...data, address: this.verifiedAddress }
			}
			this.$store.dispatch('setPickup', {
				data: data,
				callback: (response) => {
					if (response.data.result !== 'success') {
						// cmp.openError("Something went wrong...", "Please contact Leland Little for assistance.");
						this.openErrorCustomQuote(response.data.message)
						return
					}
					this.$store.commit('UPDATE_QUOTE_CANDIDATES', []) // clear out quote candidates. This is checked on the PaymentInfo screen to gray out the "Options" breadcrumb
					this.$store.dispatch('getCustomerInvoice', { invoiceId: this.currentInvoiceId }).then(response => {
						// update invoices to reflect changes in shipping
						this.$router.push({ name: 'paymentInfo' }, () => { }) // and go to payment info
					})
				}
			})
		},
		/**
		 * Gets called when artaRequestQuotes() is successful
		 * @public
		 */
		reallyGoToShippingOptions() {
			let addressWithContacts = {}
			for (let i = 0; i < this.addressFields.length; i++) {
				let field = this.addressFields[i]
				addressWithContacts[field] = this.verifiedAddress[field]
			}
			addressWithContacts.contacts = this.contacts
			this.$store.commit('UPDATE_SHIPPING_ADDRESS', addressWithContacts)
			this.$router.push({ name: 'shippingOptions' })
		},
		/**
		 * Gets called when shipping is verified by user
		 * @public
		 */
		artaRequestQuotes() {
			const data = {
				inventory_ids: this.inventory_ids,
				contacts: {}
			}
			for (var i = 0; i < this.addressFields.length; i++) {
				var field = this.addressFields[i]
				data[field] = this.verifiedAddress[field]
			}
			for (var contactField in this.contacts) {
				data.contacts[contactField] = this.contacts[contactField]
			}
			if (this.insurance !== '') {
				data.insurance = this.insurance
			}
			this.$store.dispatch('requestArtaQuotes', {
				data: data,
				callback: (response) => {
					const { data } = response
					if (this.handleQuoteResponse(data)) {
						this.reallyGoToShippingOptions()
					}
				}
			})
		},
		/**
		 * Gets called when artaRequestQuotes() is called
		 * @public
		 */
		handleQuoteResponse(data) {
			if (data.result !== 'success') {
				if (data.message_id === 'SHIPPING_MESSAGE_ID_DESTINATION_ADDRESS_FAILURE') {
					this.openError('Destination Address', data.message)
					return false
				}
				this.openErrorCustomQuote(data.message)
				return false
			}
			let payload = data.payload
			let artaHttpCode = payload.httpCode // this is the http code for lelandlittle <-> arta, not client <-> lelandlittle
			if ((artaHttpCode < 200 || artaHttpCode > 299) && Number(artaHttpCode) !== 422) {
				this.openErrorCustomQuote('Failed to connect to shipping service.')
				return false
			}
			let artaResponse = payload.response
			// CU-LL-1109 - intl address validation error
			if (typeof artaResponse.errors !== 'undefined' && typeof artaResponse.errors.destination !== 'undefined') {
				this.openError('Destination Address', data.message)
				return false
			}
			if (typeof artaResponse.bookable === 'undefined') {
				this.openErrorCustomQuote('No bookable field in response.')
				return false
			}
			if (Number(artaResponse.bookable.ready) !== 1) {
				this.openErrorCustomQuote('Bookable not ready.')
				return false
			}
			if (artaResponse.status !== 'quoted') {
				this.openErrorCustomQuote('Quote request status not quoted.')
				return false
			}
			const requestId = artaResponse.id
			let quotes = []
			for (let i = 0; i < artaResponse.quotes.length; i++) {
				let artaQuote = artaResponse.quotes[i]
				let quote = {
					id: artaQuote.id,
					description: artaQuote._display_name,
					price: artaQuote._surcharged_total,
					selected: false,
					isCustom: false,
					request: {
						id: requestId
					}
				}
				quotes.push(quote)
			}
			if (quotes.length < 1) {
				this.openErrorCustomQuote(
					'Apparently quoted and ready, but no quotes provided.'
				)
				return false
			}
			this.$store.commit('UPDATE_QUOTE_CANDIDATES', quotes)
			return true
		},
		/**
		 * Gets called when user verifies shipping
		 * @public
		 */
		proceedFromVerified() {
			if (this.pickupType === 'THIRD_PARTY') {
				this.pickupAndGoToPayment()
			} else {
				// If they can't use Arta, don't request a quote
				if (!this.canUseArta(this.invoice)) {
					return
				}
				this.artaRequestQuotes()
			}
		},
		/**
		 * Gets called when verifiedAddress is not ready
		 * @public
		 */
		avalaraValidateAddress() {
			const addressData = {}
			for (var i = 0; i < this.addressFields.length; i++) {
				var field = this.addressFields[i]
				addressData[field] = this.rawAddress[field]
			}
			this.$store.dispatch('avalaraValidateAddress', {
				address: addressData,
				callback: (response) => {
					this.verifiedCandidates.splice(0, this.verifiedCandidates.length)
					if (Number(response.data.payload.success) === 1) {
						for (i = 0; i < response.data.payload.addresses.length; i++) {
							let responseAddress = response.data.payload.addresses[i]
							let pushAddress = {}
							for (let k = 0; k < this.addressFields.length; k++) {
								let field = this.addressFields[k]
								pushAddress[field] = responseAddress[field]
							}
							this.verifiedCandidates.push(pushAddress)
						}
						this.showVerifyAddress()
						return
					}
					this.openError(
						'Please check your Address',
						'Your shipping address could not be verified. Please check that the address is correct and try again.'
					)
				}
			})
		},
		/**
		 * Gets called when user clicks Continue button
		 * @public
		 */
		goToShippingOptions() {
			// this.$v.$touch()
			if (!this.validateInputs()) {
				return
			}

			if (this.rawAddress.country === 'US' || this.rawAddress.country === 'CA') {
				// US or Canada, need to verify address with Avatax if it hasn't been done already
				if (this.verifiedAddress.isReady) {
					this.proceedFromVerified()
				} else {
					this.avalaraValidateAddress()
				}
			} else {
				for (var i = 0; i < this.addressFields.length; i++) {
					var field = this.addressFields[i]
					this.verifiedAddress[field] = this.rawAddress[field]
				}
				this.verifiedAddress.isReady = true
				this.proceedFromVerified()
			}
		},
		/**
		 * Gets called when user clicks breadcrumb item to navigate between checkout screens
		 * @public
		 */
		goToRoute(route, canNavigate) {
			if (canNavigate) {
				this.$router.push({ name: route })
			}
		},
		/**
		 * Gets called when user verifies shipping
		 * @public
		 */
		onClickedVerifyShipping(value) {
			let copyMe = this.verifiedCandidates[value]
			if (typeof copyMe === 'undefined') {
				return
			}
			for (let i = 0; i < this.addressFields.length; i++) {
				let field = this.addressFields[i]
				this.rawAddress[field] = copyMe[field]
				this.verifiedAddress[field] = copyMe[field]
			}
			this.verifiedAddress.isReady = true

			this.showVerifyShippingAddresses = false

			this.proceedFromVerified()
		},
		/**
		 * Gets called when address needs verfied
		 * @public
		 */
		showVerifyAddress() {
			this.showVerifyShippingAddresses = true
		},
		/**
		 * Gets called when user clicks arta_transit_insurance checkbox component
		 * @public
		 */
		toggleInsurance() {
			this.insurance = this.insurance === '' ? 'arta_transit_insurance' : ''
		},
		/**
		 * Gets called when AlertComponent should show
		 * @public
		 */
		openError(title, message) {
			this.error.title = ''
			this.error.message = ''
			this.error.fatal = false
			if (typeof title === typeof 'asdf') {
				this.error.title = title
			}
			if (typeof message === typeof 'asdf') {
				this.error.message = message
			}
			this.error.show = true
		},
		/**
		 * Gets called when AlertComponent should show
		 * @public
		 */
		openErrorCustomQuote(note) {
			this.error.title = 'Custom Shipping Quote Required'
			this.error.message =
				"This invoice requires a custom shipping quote. We'll notify you once your quote has been prepared."
			this.error.fatal = true
			this.error.show = true

			if (typeof note !== typeof 'asdf') {
				note = ''
			}
			var apiPayload = {
				apiEndpoint: 'shipping',
				postData: {
					action: 'custom_quote',
					inventory_ids: this.inventory_ids,
					note: note,
					contacts: {}
				}
			}
			for (var i = 0; i < this.addressFields.length; i++) {
				var field = this.addressFields[i]
				apiPayload.postData[field] = this.rawAddress[field]
			}

			for (var contactField in this.contacts) {
				apiPayload.postData.contacts[contactField] = this.contacts[contactField]
			}
			this.$store.dispatch('apiCall', apiPayload)
		},
		/**
		 * Gets called when user closes AlertComponent
		 * @public
		 */
		closeError() {
			this.error.show = false
			if (this.error.fatal) {
				this.exit()
			}
		},
		/**
		 * Gets called when user clicks "Exit"
		 * @public
		 */
		exit() {
			// apiMixin
			const referrer = localStorage.getItem('exit-referrer')
			if (referrer) {
				// If we have a exit-referrer, redirect to it
				window.location.replace(referrer)
				return
			}
			// Otherwise, redirect to the home page
			window.location.replace(this.getUrlHost(this.$store.getters.apiPath))
		},
		/**
		 * Gets called when user clicks item thumbnail image
		 * @public
		 */
		goToInventoryItem(url) {
			window.open(this.getUrlHost(this.$store.getters.apiPath) + url, '_blank')
		},
		/**
		 * Gets called when user clicks pickup_person input
		 * @public
		 */
		clickPickupPerson() {
			this.pickupType = 'THIRD_PARTY'
		},
		/**
		 * Gets called when user indicates someone will be sent to pick up
		 * @public
		 */
		clickThirdParty() {
			this.$refs.pickup_person.focus()
		},
		/**
		 * Gets called when user indicates they will pick up themselves
		 * @public
		 */
		clickInPerson() {
			this.pickupPerson = ''
		}
	}
}
</script>

<style scoped>
.dirty {
	border-color: #5a5;
}

.dirty:focus {
	outline-color: #8e8;
}

.error {
	border-color: red;
}

.error:focus {
	outline-color: #f99;
}

.shipping-insurance-container {
	padding-bottom: 1rem;
}

.shipping-insurance-container label {
	display: inline-block;
	padding-left: 1rem;
}

.radio-column {
	float: left;
	margin-right: 1em;
}

.signature-disclaimer {
	font-weight: 200;
	margin-left: 0.4em;
}
</style>
